import { ReactElement, useMemo, useRef, useState } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { Layout, SheetRowRepresentation } from '@common/api';
import { Sidebar, SidebarScroller } from '@components/layout/Sidebar';
import { FlexCol, FlexRow, FlexGrow } from '@components/layout/Flex';
import { Divider, DividerVertical } from '@components/layout/Divider';
import { TooltipWrapper } from '@components/tooltip/Tooltip';
import { Text, BasicTitle } from '@components/typography/Typography';
import {
    ChartColumnVisualization,
    ChartRowVisualization,
    ChartLabelAlign,
    ChartLabelOptions,
    ChartMarkerStyle,
    ChartSeriesType,
    ChartType,
    ChartIntegratedLegend,
    TileData,
    TileDataDimensionSlicer,
    TileDataSheetBackedWithSheet,
    TileVisualization,
    TileRepresentation,
    TileVisualizationType,
    SingleValueTileVisualization,
    ChartTileVisualization,
    TableTileVisualization,
    TableTileRowStyle,
    TableTileColumnVisualization,
    DimensionValueNameDisplay,
    NumberFormatType,
} from '@common/api';
import { ContextMenu } from '@components/context-menu/ContextMenu';
import { useContextMenuState } from '@components/context-menu/useContextMenuState';
import { patch, Update, $delete, $set, $nop } from '@use-gpu/state';
import { Toggle } from '@components/toggle/Toggle';
import {
    Angle,
    BarChartTile,
    BoldStyle,
    Eye,
    EyeCrossed,
    ItalicStyle,
    OffsetX,
    OffsetY,
    OutlineStyle,
    ThreeDots,
} from '@components/icons/FarseerIcons';
import {
    getChartAlignXOptions,
    getChartAlignYOptions,
    getChartIntegratedLegendXOptions,
    getChartIntegratedLegendYOptions,
    groupRowsByDepth,
    getCanonicalColumns,
    getCanonicalRows,
    hasIntegratedLegend,
    isAreaSeries,
    isBarSeries,
    isLineSeries,
    isPinSeries,
    isStackedSeries,
    isWaterfallSeries,
    toChartSeriesType,
    CHART_SHEET_OPTIONS,
    CHART_SHEET_OPTIONS_GROUPED,
    DEFAULT_CHART,
    STACKED_CHARTS,
    SHARED_AXIS_OPTIONS,
} from '@components/chart';
import { useDebounceHandler } from '@hooks/useDebounceHandler';
import { Switch } from '@components/switch';
import { TileDataType } from '@util/enums';
import { useSheetConfig, useSheetState } from '@features/sheet/editor/sheetState';
import { FormattingOverride } from '@features/formatting/FormattingOverride';
import { ColorPicker, ColorSwatchPicker, getTableBorderOptions } from '@features/colors/ColorPicker';
import { PalettePicker } from '@features/colors/PalettePicker';
import { useColorContext } from '@providers/ColorProvider';
import { Button } from '@components/button/Button';
import { Popover } from '@components/popover/Popover';
import useTheme, { appColors } from '@base/useTheme';
import { ToggleGroup } from '@components/toggle/ToggleGroup';
import {
    FormField,
    SwitchField,
    SelectInput,
    SelectInputField,
    TextInput,
    TextInputField,
    NumberInput,
    NumberInputField,
    ToggleField,
} from '@components/form';
import { $toggle } from '@util/patch';

import { LabelSmartFilterType, RowOrColumnLike } from '../tiles/ChartTile';
import {
    TableTileRowEdit,
    UpdateRowStyle,
    rowPathToRowKey,
    getRowGroups,
    DEFAULT_ROW_STYLE,
} from '../tiles/TableTileRowEdit';
import {
    TableTileColumnEdit,
    UpdateColumnVisualization,
    DEFAULT_COLUMN_VISUALIZATION,
} from '../tiles/TableTileColumnEdit';
import { useTileGlobalOverride } from '../tiles/useTileGlobalOverride';

import { VisualizationTypePicker } from './VisualizationTypePicker';
import { ChartLayoutPicker } from './ChartLayoutPicker';
import { SeriesTypePicker } from './SeriesTypePicker';
import { MarkerStylePicker } from './MarkerStylePicker';
import { TileMetaEdit } from './TileMetaEdit';

export type TileSidebarProps = {
    tile: TileRepresentation | undefined;
    onTileUpdate: (tile: TileRepresentation, update: Update<TileRepresentation>) => void;
    canEdit: boolean;
};

export const TileSidebar = ({ tile, onTileUpdate, canEdit }: TileSidebarProps): ReactElement | null => {
    const { LL } = useI18nContext();

    const updateTileRepresentation = (update: Update<TileRepresentation>) => {
        if (tile) onTileUpdate(tile, update);
    };

    const updateTileData = (data: Update<TileData>) => {
        updateTileRepresentation({ data });
    };

    const updateTileVisualisation = (visualization: Update<TileVisualization>) => {
        updateTileData({ visualization });
    };

    const updateTileChartVisualisation = (chart: Update<ChartTileVisualization | undefined>) => {
        updateTileVisualisation({ chart });
    };

    const tileVisualization = useMemo(
        () => (tile?.data.type === TileDataType.SHEET_BACKED ? tile?.data.visualization : null),
        [tile]
    );

    const tileChartVisualization = useMemo(
        () =>
            tile?.data.type === TileDataType.SHEET_BACKED &&
            tileVisualization &&
            tileVisualization.type === TileVisualizationType.Chart
                ? patch<ChartTileVisualization>(DEFAULT_CHART, tile?.data.visualization?.chart ?? {})
                : null,
        [tile?.data, tileVisualization]
    );

    const isTile = !!tile;
    const isSlicer = tile?.data.type === TileDataType.DIMENSION_SLICER;
    const isSheet = tile?.data.type === TileDataType.SHEET_BACKED;
    const isChart =
        tile?.data.type === TileDataType.SHEET_BACKED && tile?.data.visualization.type === TileVisualizationType.Chart;
    const isTable =
        tile?.data.type === TileDataType.SHEET_BACKED && tile?.data.visualization.type === TileVisualizationType.Table;
    const isSingleValue =
        tile?.data.type === TileDataType.SHEET_BACKED &&
        tile?.data.visualization.type === TileVisualizationType.SingleValue;

    const tileDataSheet = tile?.data as TileDataSheetBackedWithSheet | undefined;
    const tileDataSlicer = tile?.data as TileDataDimensionSlicer | undefined;

    return (
        <Sidebar $white>
            {isTile ? (
                <FlexCol style={{ height: '100%' }}>
                    <BasicTitle style={{ padding: '16px' }}>{LL.EDITING_TILE()}</BasicTitle>
                    <SidebarScroller>
                        <FlexCol $gap={8}>
                            <VisualizationTypePicker
                                tile={tile}
                                onChange={updateTileRepresentation}
                                canEdit={canEdit}
                            />

                            {tileChartVisualization ? (
                                <>
                                    <ChartLayoutPicker
                                        chart={tileChartVisualization}
                                        onChange={updateTileChartVisualisation}
                                        canEdit={canEdit}
                                    />

                                    <Divider />
                                </>
                            ) : null}

                            <TileMetaEdit tile={tile} onChange={updateTileRepresentation} canEdit={canEdit} />

                            {isSheet && tileDataSheet ? (
                                <>
                                    <Divider />
                                    <TileBarEditor
                                        sheetPath={`${tileDataSheet.sheet.id}`}
                                        data={tileDataSheet}
                                        updateTileData={updateTileData}
                                        canEdit={canEdit}
                                        hasVisibilityOptions={isChart || isTable}
                                    />
                                </>
                            ) : null}

                            {isTable && tileDataSheet ? (
                                <TableStyleEditor
                                    sheetPath={`${tileDataSheet.sheet.id}`}
                                    canEdit={canEdit}
                                    data={tileDataSheet}
                                    table={tileDataSheet.visualization.table ?? {}}
                                    updateVisualisation={updateTileVisualisation}
                                />
                            ) : null}

                            {isSingleValue && tileDataSheet ? (
                                <SingleValueEditor
                                    key={tile.id}
                                    canEdit={canEdit}
                                    visualization={tileDataSheet.visualization.singleValue}
                                    updateVisualisation={updateTileVisualisation}
                                />
                            ) : null}

                            {isSlicer && tileDataSlicer ? (
                                <>
                                    <Divider />
                                    <FlexRow $gap={5} $wrap>
                                        <Toggle
                                            title={LL.NAME()}
                                            checked={tileDataSlicer.displayType === DimensionValueNameDisplay.Name}
                                            onChange={() =>
                                                updateTileData({ displayType: DimensionValueNameDisplay.Name })
                                            }
                                        />
                                        <Toggle
                                            title={LL.DESCRIPTION()}
                                            checked={
                                                tileDataSlicer.displayType === DimensionValueNameDisplay.Description
                                            }
                                            onChange={() =>
                                                updateTileData({ displayType: DimensionValueNameDisplay.Description })
                                            }
                                        />
                                        <Toggle
                                            title={LL.BOTH()}
                                            checked={tileDataSlicer.displayType === DimensionValueNameDisplay.Both}
                                            onChange={() =>
                                                updateTileData({ displayType: DimensionValueNameDisplay.Both })
                                            }
                                        />
                                    </FlexRow>
                                </>
                            ) : null}

                            {isChart && tileDataSheet && tileChartVisualization ? (
                                <>
                                    <Divider />
                                    <ChartEditor
                                        sheetPath={`${tileDataSheet.sheet.id}`}
                                        data={tileDataSheet}
                                        canEdit={canEdit}
                                        chart={tileChartVisualization}
                                        updateChartVisualisation={updateTileChartVisualisation}
                                    />
                                </>
                            ) : null}
                        </FlexCol>

                        <div style={{ paddingBottom: 32 }} />
                    </SidebarScroller>
                </FlexCol>
            ) : (
                <FlexCol $flexGrow $align="center" $justify="center">
                    <div>{LL.NO_TILE_SELECTED()}</div>
                </FlexCol>
            )}
        </Sidebar>
    );
};

export function TileBarEditor({
    sheetPath,
    data,
    updateTileData,
    canEdit,
    hasVisibilityOptions,
}: {
    sheetPath: string;
    data: TileDataSheetBackedWithSheet;
    updateTileData: (u: Update<TileData>) => void;
    canEdit: boolean;
    hasVisibilityOptions: boolean;
}): ReactElement | null {
    const { LL } = useI18nContext();

    const globalOverride = useTileGlobalOverride(data);
    const { sheetConfig } = useSheetConfig(sheetPath, false, canEdit, globalOverride);

    const hasTileTimeline = sheetConfig.columns.intervalToolbar.enabled;

    const toggleOptions = [
        { title: LL.TILE(), value: false },
        { title: LL.DASHBOARD(), value: true },
    ];

    return (
        <FlexCol $gap={4}>
            <FormField label={LL.USE_SORT_FROM()} inline>
                <FlexRow $gap={4}>
                    {hasVisibilityOptions && !data.useDashboardSort && (
                        <TooltipWrapper label={LL.SHOW_TILE_SORT()}>
                            <Button
                                $type="quaternary"
                                icon={data.showTileSort ? Eye : EyeCrossed}
                                disabled={!canEdit}
                                onClick={() => updateTileData({ showTileSort: $toggle() })}
                            />
                        </TooltipWrapper>
                    )}
                    <ToggleGroup
                        $size="small"
                        value={data.useDashboardSort}
                        onChange={(useDashboardSort) =>
                            updateTileData({
                                useDashboardSort,
                            })
                        }
                    >
                        {toggleOptions.map((props, i) => (
                            <ToggleGroup.Toggle key={i} {...props} />
                        ))}
                    </ToggleGroup>
                </FlexRow>
            </FormField>

            <FormField label={LL.USE_FILTERS_FROM()} inline>
                <FlexRow $gap={4}>
                    {hasVisibilityOptions && !data.useDashboardFilters && (
                        <TooltipWrapper label={LL.SHOW_TILE_FILTERS()}>
                            <Button
                                $type="quaternary"
                                icon={data.showTileFilters ? Eye : EyeCrossed}
                                disabled={!canEdit}
                                onClick={() => updateTileData({ showTileFilters: $toggle() })}
                            />
                        </TooltipWrapper>
                    )}
                    <ToggleGroup
                        $size="small"
                        value={data.useDashboardFilters}
                        onChange={(useDashboardFilters) =>
                            updateTileData({
                                useDashboardFilters,
                            })
                        }
                    >
                        {toggleOptions.map((props, i) => (
                            <ToggleGroup.Toggle key={i} {...props} />
                        ))}
                    </ToggleGroup>
                </FlexRow>
            </FormField>

            <FormField label={LL.USE_TIMELINE_FROM()} inline>
                <FlexRow $gap={4}>
                    {hasVisibilityOptions && !data.useDashboardTimeline && (
                        <TooltipWrapper label={LL.SHOW_TILE_TIMELINE()}>
                            <Button
                                $type="quaternary"
                                icon={data.showTileTimeline ? Eye : EyeCrossed}
                                disabled={!canEdit || !hasTileTimeline}
                                onClick={() => updateTileData({ showTileTimeline: $toggle() })}
                            />
                        </TooltipWrapper>
                    )}
                    <ToggleGroup
                        $size="small"
                        disabled={hasTileTimeline}
                        value={data.useDashboardTimeline}
                        onChange={(useDashboardTimeline) =>
                            updateTileData({
                                useDashboardTimeline,
                            })
                        }
                    >
                        {toggleOptions.map((props, i) => (
                            <ToggleGroup.Toggle key={i} {...props} />
                        ))}
                    </ToggleGroup>
                </FlexRow>
            </FormField>
        </FlexCol>
    );
}

export function ChartEditor({
    sheetPath,
    data,
    canEdit,
    chart,
    updateChartVisualisation,
}: {
    sheetPath: string;
    data: TileDataSheetBackedWithSheet;
    canEdit: boolean;
    chart: ChartTileVisualization;
    updateChartVisualisation: (visualization: Update<ChartTileVisualization | undefined>) => void;
}): ReactElement | null {
    const { LL } = useI18nContext();
    const { colors } = useTheme();

    const globalOverride = useTileGlobalOverride(data);
    const localOverride = useMemo(
        () => ({
            ...globalOverride,
            ...CHART_SHEET_OPTIONS,
            ...(chart.series.groupByDepth ? CHART_SHEET_OPTIONS_GROUPED : undefined),
        }),
        [globalOverride, chart.series.groupByDepth]
    );

    const sheetContext = useSheetState(sheetPath, false, canEdit, localOverride);
    const { defaultPalette } = useColorContext();

    const updateChartRowVisualization = (data: { [key: string]: Update<ChartRowVisualization> }) => {
        updateChartVisualisation({
            rowVisualizationByPath: data,
        });
    };

    const updateChartColumnVisualization = (data: { [key: string]: Update<ChartColumnVisualization> }) => {
        updateChartVisualisation({
            columnVisualizationByPath: data,
        });
    };

    const { rows, columns } = sheetContext.state.sheetData;

    const {
        layout,
        labels: { showLabels, labelAlign },
        spacing: { barSpacing, integratedLegendX, integratedLegendY },
        display: {
            showValueAxis,
            showDomainAxis,
            showLegend,
            showTotals,
            showIntegratedLegend,
            integratedLegendPosition,
            domainAxisColor,
            domainAxisBorderStyle,
            domainAxisLabelAngle,
            domainAxisLabelCompact,
            shareAxis,
            shareAxisGroup,
        },
        series: { transposeData, normalizeData, keepEmptyDomain, keepEmptyValue, groupByDepth },
    } = chart;
    const { numberColumns } = getCanonicalColumns(columns);

    const groupedRows = groupByDepth ? groupRowsByDepth(rows).groupedRows : null;
    const canonicalRows = groupedRows ? getCanonicalRows(groupedRows).rows : rows;

    const asRows = transposeData ? numberColumns : canonicalRows;
    const asColumns = transposeData ? canonicalRows : numberColumns;

    const asRowsLabel = transposeData ? LL.CHART_COLUMN_OPTIONS() : LL.CHART_ROW_OPTIONS();
    const asColumnsLabel = transposeData ? LL.CHART_ROW_OPTIONS() : LL.CHART_COLUMN_OPTIONS();

    const isComboChart = chart.type === ChartType.ComboChart;
    const isPieChart = chart.type === ChartType.PieChart;

    const chartSeriesType = toChartSeriesType(chart.type);
    const seriesTypes = isComboChart
        ? asRows.map((row: RowOrColumnLike) => {
              const currRowVisualization = chart.rowVisualizationByPath[row.key];
              const currentType = currRowVisualization?.type || ChartSeriesType.BarChart;
              return currentType;
          })
        : [chartSeriesType];

    const isVertical = layout === Layout.Vertical;

    const hasBarSeries = seriesTypes.some(isBarSeries);
    const hasWaterfallSeries = seriesTypes.some(isWaterfallSeries);
    const hasStackedSeries = seriesTypes.some(isStackedSeries);
    const hasAreaSeries = seriesTypes.some(isAreaSeries);
    const hasPinSeries = seriesTypes.some(isPinSeries);

    const isAllStacked = STACKED_CHARTS.includes(chart.type);
    const isStacked = isAllStacked || hasStackedSeries;
    const canStack = isAllStacked || isComboChart;

    const hasAxis = !isPieChart;
    const hasTotal = hasWaterfallSeries;
    const hasColumns = hasTotal;

    const hasLabelOutline = labelAlign === ChartLabelAlign.Middle && (hasBarSeries || hasAreaSeries || isPieChart);

    const [rowColors, paletteColors] = useMemo(() => {
        const rowColors = asRows.map((row: RowOrColumnLike, rowIdx: number) => {
            const currRowVisualization = chart.rowVisualizationByPath[row.key];
            const defaultColor = defaultPalette[rowIdx % defaultPalette.length];
            const rowColor = currRowVisualization?.color;
            return rowColor ?? { solid: defaultColor };
        });

        const paletteColors = rowColors.slice(0, 30).map((r) => r.solid ?? '#000');

        return [rowColors, paletteColors];
    }, [chart, asRows, defaultPalette]);

    const updateChartRowPalette = (paletteColors: string[]) => {
        const op: Record<string, Update<ChartRowVisualization>> = {};
        for (const [i, row] of asRows.entries()) {
            op[row.key] = { color: { solid: paletteColors[i % paletteColors.length] } };
        }
        updateChartRowVisualization(op);
    };

    const supportsIntegratedLegend = hasIntegratedLegend(chart.type);
    const hasOverlappingLegend =
        supportsIntegratedLegend &&
        showIntegratedLegend &&
        (isVertical
            ? integratedLegendPosition !== ChartIntegratedLegend.Start
            : integratedLegendPosition !== ChartIntegratedLegend.End);
    const integratedLegendOptions = isVertical
        ? getChartIntegratedLegendYOptions(LL)
        : getChartIntegratedLegendXOptions(LL);

    const integratedLegendNode = showIntegratedLegend ? (
        <FlexRow $gap={8}>
            <ToggleGroup
                $size="small"
                value={integratedLegendPosition}
                onChange={(integratedLegendPosition) =>
                    updateChartVisualisation({
                        display: { integratedLegendPosition },
                    })
                }
            >
                {integratedLegendOptions.map((props) => (
                    <ToggleGroup.Toggle key={props.value} {...props} />
                ))}
            </ToggleGroup>
            <NumberInput
                type="int"
                value={isVertical ? integratedLegendY : integratedLegendX}
                empty={isVertical ? DEFAULT_CHART.spacing.integratedLegendY : DEFAULT_CHART.spacing.integratedLegendX}
                step={10}
                upDown
                nullable
                onChange={(spacing) =>
                    updateChartVisualisation({
                        spacing: isVertical ? { integratedLegendY: spacing } : { integratedLegendX: spacing },
                    })
                }
                width={72}
                prefix={isVertical ? <OffsetY color={colors.neutral.w700} /> : <OffsetX color={colors.neutral.w700} />}
            />
        </FlexRow>
    ) : null;

    return (
        <FlexCol $gap={8}>
            <FlexCol>
                <SwitchField
                    label={LL.TRANSPOSE_DATA()}
                    disabled={!canEdit}
                    value={transposeData}
                    onChange={(transposeData) => updateChartVisualisation({ series: { transposeData } })}
                />
                {isAllStacked ? (
                    <SwitchField
                        label={LL.NORMALIZE_TO_PERCENT()}
                        disabled={!canEdit}
                        value={normalizeData}
                        onChange={(normalizeData) => updateChartVisualisation({ series: { normalizeData } })}
                    />
                ) : null}
                <SwitchField
                    label={transposeData ? LL.SHOW_EMPTY_ROWS() : LL.SHOW_EMPTY_COLUMNS()}
                    disabled={!canEdit}
                    value={keepEmptyValue}
                    onChange={(keepEmptyValue) => updateChartVisualisation({ series: { keepEmptyValue } })}
                />
                <SwitchField
                    label={transposeData ? LL.SHOW_EMPTY_COLUMNS() : LL.SHOW_EMPTY_ROWS()}
                    disabled={!canEdit}
                    value={keepEmptyDomain}
                    onChange={(keepEmptyDomain) => updateChartVisualisation({ series: { keepEmptyDomain } })}
                />
            </FlexCol>
            <Divider />
            <Text>{LL.VISUALISATION_OPTIONS()}</Text>

            <FlexCol>
                {hasAxis ? (
                    <SwitchField
                        label={LL.SHOW_VALUE_AXIS()}
                        disabled={!canEdit || hasOverlappingLegend}
                        value={showValueAxis && !hasOverlappingLegend}
                        onChange={(showValueAxis) => updateChartVisualisation({ display: { showValueAxis } })}
                    />
                ) : null}
                {hasAxis ? (
                    <SwitchField
                        label={LL.SHOW_CATEGORY_AXIS_LABELS()}
                        disabled={!canEdit}
                        value={showDomainAxis}
                        onChange={(showDomainAxis) => updateChartVisualisation({ display: { showDomainAxis } })}
                    />
                ) : null}
                {canStack ? (
                    <SwitchField
                        label={LL.SHOW_TOTALS()}
                        disabled={!canEdit || !isStacked}
                        value={showTotals}
                        onChange={(showTotals) => updateChartVisualisation({ display: { showTotals } })}
                    />
                ) : null}
                <SwitchField
                    label={LL.SHOW_LEGEND()}
                    disabled={!canEdit}
                    value={showLegend}
                    onChange={(showLegend) => updateChartVisualisation({ display: { showLegend } })}
                />

                {supportsIntegratedLegend ? (
                    <>
                        <SwitchField
                            label={LL.SHOW_INTEGRATED_LEGEND()}
                            disabled={!canEdit}
                            value={showIntegratedLegend}
                            onChange={(showIntegratedLegend) =>
                                updateChartVisualisation({ display: { showIntegratedLegend } })
                            }
                        />

                        {integratedLegendNode}
                    </>
                ) : null}
            </FlexCol>

            {!isPieChart ? (
                <>
                    <Divider />

                    <FormField label={LL.CATEGORY_AXIS()} inline>
                        <FlexRow $gap={8} $align="center">
                            <ToggleGroup
                                $size="small"
                                value={domainAxisBorderStyle}
                                onChange={(domainAxisBorderStyle) => {
                                    updateChartVisualisation({
                                        display: { domainAxisBorderStyle },
                                    });
                                }}
                            >
                                {getTableBorderOptions(LL).map((option) => (
                                    <ToggleGroup.Toggle
                                        label={option.label}
                                        icon={option.icon}
                                        key={option.value}
                                        value={option.value}
                                    />
                                ))}
                            </ToggleGroup>
                            <ColorPicker
                                disabled={!canEdit}
                                value={domainAxisColor}
                                onChange={(domainAxisColor) =>
                                    updateChartVisualisation({
                                        display: { domainAxisColor },
                                    })
                                }
                            />
                        </FlexRow>
                    </FormField>

                    {hasAxis && showDomainAxis ? (
                        <>
                            <SwitchField
                                label={LL.COMPACT_AXIS_LABELS()}
                                value={domainAxisLabelCompact}
                                onChange={(domainAxisLabelCompact) =>
                                    updateChartVisualisation({ display: { domainAxisLabelCompact } })
                                }
                            />
                            <NumberInputField
                                inline
                                label={LL.CATEGORY_LABEL_ANGLE()}
                                type="int"
                                value={domainAxisLabelAngle}
                                empty={0}
                                step={1}
                                upDown
                                onChange={(domainAxisLabelAngle) => {
                                    updateChartVisualisation({
                                        display: { domainAxisLabelAngle },
                                    });
                                }}
                                width={80}
                                prefix={<Angle color={colors.neutral.w700} />}
                                suffix={<span>°&nbsp;</span>}
                            />
                        </>
                    ) : null}

                    {hasBarSeries || hasWaterfallSeries || hasPinSeries ? (
                        <>
                            <NumberInputField
                                inline
                                label={LL.BAR_SPACING()}
                                disabled={!canEdit}
                                value={barSpacing}
                                type="int"
                                empty={0}
                                min={-100}
                                max={100}
                                step={10}
                                width={90}
                                upDown
                                prefix={<BarChartTile color={colors.neutral.w700} />}
                                suffix={<span>%&nbsp;&nbsp;</span>}
                                onChange={(barSpacing) => updateChartVisualisation({ spacing: { barSpacing } })}
                            />
                        </>
                    ) : null}

                    <SwitchField
                        label={LL.USE_SHARED_SCALE()}
                        value={shareAxis}
                        onChange={(shareAxis) => updateChartVisualisation({ display: { shareAxis } })}
                        suffix={
                            shareAxis ? (
                                <SelectInput
                                    width={40}
                                    size="small"
                                    options={SHARED_AXIS_OPTIONS}
                                    value={shareAxisGroup}
                                    onChange={(shareAxisGroup) =>
                                        updateChartVisualisation({ display: { shareAxisGroup } })
                                    }
                                />
                            ) : null
                        }
                    />
                </>
            ) : null}

            <Divider />

            <ChartLabelFormatting
                layout={layout}
                title={LL.VALUE_LABELS()}
                hasAngle
                hasOutline={hasLabelOutline}
                hasX={!isPieChart}
                hasY={!isPieChart}
                value={chart?.labels ?? {}}
                onUpdate={(update: Update<ChartLabelOptions>) => updateChartVisualisation({ labels: update ?? $nop() })}
            />

            <Divider />

            <FlexCol $gap={8}>
                <FlexRow $gap={8}>
                    <Text>{asRowsLabel}</Text>
                    <FlexGrow />
                    <PalettePicker
                        value={paletteColors}
                        onChange={(paletteColors) => updateChartRowPalette(paletteColors)}
                    />
                </FlexRow>
                <FlexCol $gap={4}>
                    {asRows.map((row: RowOrColumnLike, rowIdx: number) => {
                        const currRowVisualization = chart.rowVisualizationByPath[row.key];
                        const currentColor = rowColors[rowIdx];
                        const currentType = currRowVisualization?.type || ChartSeriesType.BarChart;
                        const currentMarker = currRowVisualization?.marker || ChartMarkerStyle.Dot;

                        const currentSeriesType = isComboChart ? currentType : chartSeriesType;
                        const hasMarker = isAreaSeries(currentSeriesType) || isLineSeries(currentSeriesType);

                        const rowName = row.cells?.[0]?.value?.string ?? row.name;

                        return (
                            <FlexRow key={row.key} $gap={4} $center>
                                <Text style={{ flexGrow: 1, lineHeight: '24px' }}>
                                    {rowName.length > 12 ? rowName.substring(0, 15) + '...' : rowName}
                                </Text>

                                {hasMarker && (
                                    <>
                                        <MarkerStylePicker
                                            value={currentMarker}
                                            onChange={(marker) =>
                                                updateChartRowVisualization({
                                                    [row.key]: { marker },
                                                })
                                            }
                                        />
                                    </>
                                )}

                                {isComboChart && (
                                    <>
                                        <SeriesTypePicker
                                            value={currentType}
                                            onChange={(type) =>
                                                updateChartRowVisualization({
                                                    [row.key]: { type },
                                                })
                                            }
                                        />
                                    </>
                                )}

                                <ColorSwatchPicker
                                    value={currentColor}
                                    onChange={(color) =>
                                        updateChartRowVisualization({
                                            [row.key]: {
                                                color,
                                            },
                                        })
                                    }
                                    hasBorder
                                    hasPattern
                                    hasColorBy
                                />

                                {showLabels || hasWaterfallSeries ? (
                                    <Popover placement="left-start">
                                        <Popover.Trigger>
                                            <Button $size="small" icon={<ThreeDots />} $type="quaternary" />
                                        </Popover.Trigger>
                                        <Popover.Content width={360} $padding="7px" noFocus>
                                            <FlexCol
                                                // Extra padding for selection outline spill
                                                style={{ padding: 1 }}
                                            >
                                                {hasWaterfallSeries ? (
                                                    <SwitchField
                                                        label={LL.ACCUMULATE_DATA()}
                                                        disabled={!canEdit}
                                                        value={currRowVisualization?.accumulateData ?? true}
                                                        onChange={(accumulateData) =>
                                                            updateChartRowVisualization({
                                                                [row.key]: { accumulateData },
                                                            })
                                                        }
                                                    />
                                                ) : null}
                                                <ChartLabelFormatting
                                                    layout={layout}
                                                    title={LL.ROW_VALUE_LABELS()}
                                                    hasAngle
                                                    hasOutline={hasLabelOutline}
                                                    hasX={!isPieChart}
                                                    hasY={!isPieChart}
                                                    value={currRowVisualization?.labels ?? {}}
                                                    onUpdate={(update: Update<ChartLabelOptions>) =>
                                                        updateChartRowVisualization({
                                                            [row.key]: { labels: update ?? $nop() },
                                                        })
                                                    }
                                                />
                                            </FlexCol>
                                        </Popover.Content>
                                    </Popover>
                                ) : null}
                            </FlexRow>
                        );
                    })}
                </FlexCol>
            </FlexCol>

            {hasColumns ? (
                <>
                    <Divider />

                    <FlexCol $gap={15}>
                        <Text>{asColumnsLabel}</Text>

                        <FlexCol $gap={4}>
                            {asColumns.map((column: RowOrColumnLike, columnIdx: number) => {
                                const currColumnVisualization = chart.columnVisualizationByPath[column.key];

                                return (
                                    <ToggleField
                                        key={columnIdx}
                                        size="small"
                                        title={LL.TOTAL()}
                                        label={column.name}
                                        disabled={columnIdx === 0}
                                        value={currColumnVisualization?.total || columnIdx === 0}
                                        onChange={() =>
                                            updateChartColumnVisualization({
                                                [column.key]: { total: !currColumnVisualization?.total },
                                            })
                                        }
                                    />
                                );
                            })}
                        </FlexCol>
                    </FlexCol>
                </>
            ) : null}
        </FlexCol>
    );
}

type ChartLabelFormattingProps = {
    layout: Layout | null;
    title: string;
    hasAngle?: boolean;
    hasOutline?: boolean;
    hasX?: boolean;
    hasY?: boolean;
    value: ChartLabelOptions;
    onUpdate: (update: Update<ChartLabelOptions>) => void;
};

export const ChartLabelFormatting = (props: ChartLabelFormattingProps) => {
    const { layout, title, value, onUpdate, hasAngle, hasOutline, hasX, hasY } = props;
    const { colors } = useTheme();

    const { LL } = useI18nContext();

    const showLabels = value?.showLabels ?? true;

    const labelBold = value?.labelBold ?? false;
    const labelItalic = value?.labelItalic ?? false;
    const labelOutline = value?.labelOutline ?? true;

    // remove until supported in echarts
    //const labelUnderline = value?.labelUnderline ?? false;

    const labelColor = value?.labelColor || appColors.ui.dark;
    const labelFontSize = value?.labelFontSize;
    const labelAngle = value?.labelAngle;
    const labelDX = value?.labelDX;
    const labelDY = value?.labelDY;

    const labelAlign = value?.labelAlign ?? ChartLabelAlign.Auto;

    const alignOptions = layout === Layout.Vertical ? getChartAlignXOptions(LL) : getChartAlignYOptions(LL);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { showLabels: _, ...rest } = value;
    const hasValues = Object.keys(rest).length > 0;

    return (
        <FlexCol $gap={8}>
            <SwitchField
                label={title}
                value={showLabels}
                onChange={() =>
                    onUpdate({
                        showLabels: !showLabels,
                    })
                }
                suffix={
                    showLabels && hasValues ? (
                        <FlexRow $gap={8}>
                            <Button
                                title={LL.RESET_STYLES()}
                                $size="small"
                                $type="tertiary"
                                onClick={() => {
                                    onUpdate($set({ showLabels: true }) as Update<ChartLabelOptions>);
                                }}
                            />
                            <div />
                        </FlexRow>
                    ) : null
                }
            />

            {showLabels ? (
                <>
                    <FlexRow $align="center">
                        <NumberInput
                            type="int"
                            size="small"
                            value={labelFontSize}
                            nullable
                            empty={12}
                            min={6}
                            max={144}
                            step={1}
                            upDown
                            onChange={(labelFontSize) => {
                                onUpdate({
                                    labelFontSize,
                                });
                            }}
                            width={60}
                        />

                        <DividerVertical />

                        <TooltipWrapper label={LL.BOLD()}>
                            <Toggle
                                $size="small"
                                icon={BoldStyle}
                                checked={labelBold}
                                onChange={(labelBold) =>
                                    onUpdate({
                                        labelBold,
                                    })
                                }
                            />
                        </TooltipWrapper>
                        <TooltipWrapper label={LL.ITALIC()}>
                            <Toggle
                                $size="small"
                                icon={ItalicStyle}
                                checked={labelItalic}
                                onChange={(labelItalic) =>
                                    onUpdate({
                                        labelItalic,
                                    })
                                }
                            />
                        </TooltipWrapper>
                        {hasOutline ? (
                            <TooltipWrapper label={LL.OUTLINED()}>
                                <Toggle
                                    $size="small"
                                    icon={OutlineStyle}
                                    checked={labelOutline}
                                    onChange={(labelOutline) =>
                                        onUpdate({
                                            labelOutline,
                                        })
                                    }
                                />
                            </TooltipWrapper>
                        ) : null}
                        {/* remove until supported in echarts */}
                        {/*
                        <TooltipWrapper label={LL.UNDERLINED()}>
                        <Toggle
                            $size="small"
                            icon={UnderlineStyle}
                            checked={labelUnderline}
                            onChange={(labelUnderline) =>
                                onUpdate({
                                    labelUnderline,
                                })
                            }
                        />
                        </TooltipWrapper>
                        */}

                        <FlexGrow />
                        <ColorPicker
                            value={labelColor}
                            onChange={(labelColor) =>
                                onUpdate({
                                    labelColor,
                                })
                            }
                        />
                    </FlexRow>

                    <FormField label={LL.POSITION()}>
                        <FlexRow $gap={4}>
                            {layout != null ? (
                                <ToggleGroup
                                    $size="small"
                                    value={labelAlign}
                                    onChange={(labelAlign) =>
                                        onUpdate({
                                            labelAlign,
                                        })
                                    }
                                >
                                    {alignOptions.map((props) => (
                                        <ToggleGroup.Toggle key={props.value} {...props} />
                                    ))}
                                </ToggleGroup>
                            ) : null}

                            {hasAngle ? (
                                <NumberInput
                                    type="int"
                                    value={labelAngle}
                                    empty={0}
                                    step={1}
                                    upDown
                                    nullable
                                    onChange={(labelAngle) => {
                                        onUpdate({
                                            labelAngle,
                                        });
                                    }}
                                    width={80}
                                    prefix={<Angle color={colors.neutral.w700} />}
                                    suffix={<span>°&nbsp;</span>}
                                />
                            ) : null}

                            {hasX ? (
                                <NumberInput
                                    type="int"
                                    value={labelDX}
                                    empty={0}
                                    step={1}
                                    upDown
                                    nullable
                                    onChange={(labelDX) => {
                                        onUpdate({
                                            labelDX,
                                        });
                                    }}
                                    width={65}
                                    prefix={<OffsetX color={colors.neutral.w700} />}
                                />
                            ) : null}

                            {hasY ? (
                                <NumberInput
                                    type="int"
                                    value={labelDY}
                                    empty={0}
                                    step={1}
                                    upDown
                                    nullable
                                    onChange={(labelDY) => {
                                        onUpdate({
                                            labelDY,
                                        });
                                    }}
                                    width={65}
                                    prefix={<OffsetY color={colors.neutral.w700} />}
                                />
                            ) : null}
                        </FlexRow>
                    </FormField>

                    <SelectInputField
                        label={LL.SMART_FILTERING()}
                        labelSize="large"
                        size="small"
                        value={value?.labelSmartFilter || 'None'}
                        onChange={(labelSmartFilter) => {
                            onUpdate({
                                labelSmartFilter,
                            });
                        }}
                        options={[
                            ...Object.entries(LabelSmartFilterType).map(([key, value]) => ({
                                label: key === 'NONE' ? LL.NONE() : key === 'CHANGE' ? LL.SMART_FILTER_CHANGE() : value,
                                value: value as NumberFormatType,
                            })),
                        ]}
                    />

                    <FormattingOverride
                        hasPolarity
                        numberFormat={value?.labelFormat}
                        onChange={(labelFormat) => {
                            onUpdate({
                                labelFormat: labelFormat ?? $delete(),
                            });
                        }}
                    />
                </>
            ) : null}
        </FlexCol>
    );
};

export function SingleValueEditor({
    canEdit,
    visualization,
    updateVisualisation,
}: {
    canEdit: boolean;
    visualization?: SingleValueTileVisualization;
    updateVisualisation: (visualization: Update<TileVisualization>) => void;
}): ReactElement | null {
    const { LL } = useI18nContext();
    const [comparisonCustomText, setComparisonCustomText] = useState(visualization?.comparisonCustomText || '');
    useDebounceHandler(
        () => {
            if (visualization?.comparisonCustomText !== comparisonCustomText) {
                const newSVVisualisation: SingleValueTileVisualization = patch(visualization || {}, {
                    comparisonCustomText: comparisonCustomText,
                });
                updateVisualisation({ singleValue: newSVVisualisation });
            }
        },
        comparisonCustomText,
        500
    );

    const [dataEntryButtonStep, setDataEntryButtonStep] = useState(visualization?.dataEntryButtonStep || '10%');
    useDebounceHandler(
        () => {
            if (visualization && visualization.dataEntryButtonStep !== dataEntryButtonStep) {
                const newSVVisualisation: SingleValueTileVisualization = patch(visualization, {
                    dataEntryButtonStep: dataEntryButtonStep,
                });
                updateVisualisation({ singleValue: newSVVisualisation });
            }
        },
        dataEntryButtonStep,
        500
    );

    return (
        <FlexCol $gap={8}>
            <div />
            <Divider />
            <TextInputField
                label={LL.COMPARISON_CUSTOM_TEXT()}
                value={comparisonCustomText}
                placeholder={LL.LEAVE_EMPTY_FOR_AUTOGENERATED()}
                onChange={(s) => setComparisonCustomText(s)}
                disabled={!canEdit}
            />
            <FlexCol>
                <SwitchField
                    label={LL.DATA_ENTRY()}
                    disabled={!canEdit}
                    value={visualization?.dataEntry}
                    onChange={() => updateVisualisation({ singleValue: { dataEntry: !visualization?.dataEntry } })}
                />
                <FormField label={LL.SHOW_BUTTONS()} inline>
                    <TextInput
                        value={dataEntryButtonStep}
                        placeholder={LL.STEP()}
                        onChange={(s) => setDataEntryButtonStep(s)}
                        disabled={!canEdit || !visualization?.showDataEntryButtons}
                        width={80}
                    />
                    <Switch
                        checked={visualization?.showDataEntryButtons}
                        onChange={() =>
                            updateVisualisation({
                                singleValue: { showDataEntryButtons: !visualization?.showDataEntryButtons },
                            })
                        }
                        disabled={!canEdit || !visualization?.dataEntry}
                    />
                </FormField>
            </FlexCol>
            <FormattingOverride
                hasPolarity
                numberFormat={visualization?.format}
                onChange={(f) =>
                    updateVisualisation({
                        singleValue: {
                            format: f ?? $delete(),
                        },
                    })
                }
            />
        </FlexCol>
    );
}

export function TableStyleEditor({
    sheetPath,
    canEdit,
    data,
    table,
    updateVisualisation,
}: {
    sheetPath: string;
    canEdit: boolean;
    data: TileDataSheetBackedWithSheet;
    table: TableTileVisualization;
    updateVisualisation: (visualization: Update<TileVisualization>) => void;
}) {
    const { LL } = useI18nContext();

    const globalOverride = useTileGlobalOverride(data);
    const sheetContext = useSheetState(sheetPath, false, canEdit, globalOverride);

    const {
        state: {
            sheetData: { rows, columns },
        },
    } = sheetContext;

    const rowStyles = table?.rowStyles ?? {};
    const columnVisualizationByKey = table?.columnVisualizationByKey ?? {};

    const globalColumnVisualization = patch(DEFAULT_COLUMN_VISUALIZATION, columnVisualizationByKey['']);

    const hasWaterfallColumns = columns.some(
        (c) => columnVisualizationByKey[c.key]?.type == ChartSeriesType.WaterfallChart
    );
    const rowGroups = getRowGroups(rows);

    const btnRef = useRef<HTMLButtonElement>(null);
    const { menuState } = useContextMenuState({ click: 'left' });

    const updateRowStyles = (rowStyles: Update<Record<string, TableTileRowStyle> | undefined>) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const update: Update<any> = { table: { rowStyles } };
        updateVisualisation(update);
    };

    const updateColumnVisualizations = (
        columnVisualizationByKey: Update<Record<string, TableTileColumnVisualization> | undefined>
    ) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const update: Update<any> = { table: { columnVisualizationByKey } };
        updateVisualisation(update);
    };

    const updateGlobalColumnVisualization = (update: Update<TableTileColumnVisualization>) => {
        updateColumnVisualizations({ '': update });
    };

    const handleResetGlobal = () => {
        updateVisualisation({
            table: {
                rowStyles: { '': $delete() },
                columnVisualizationByKey: { '': $delete() },
            },
        });
    };

    const handleResetRows = () => {
        updateRowStyles($set({ '': rowStyles[''] ?? {} }) as UpdateRowStyle);
    };

    const handleResetColumns = () => {
        updateColumnVisualizations($set({ '': columnVisualizationByKey[''] ?? {} }) as UpdateColumnVisualization);
    };

    const globalReset = (
        <FormField label={LL.GENERAL_STYLING()} inline>
            <Button
                style={{ height: 24, width: 24 }}
                icon={ThreeDots}
                $type="quaternary"
                onClick={() => menuState.setOpen(true)}
                ref={btnRef}
            />
            <ContextMenu {...menuState} anchorRef={btnRef} placement="bottom-end">
                <ContextMenu.Item
                    item={{
                        label: LL.RESET_GENERAL_STYLES(),
                        action: handleResetGlobal,
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.RESET_ALL_PER_ROW_STYLES(),
                        action: handleResetRows,
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.RESET_ALL_PER_COLUMN_STYLES(),
                        action: handleResetColumns,
                    }}
                />
            </ContextMenu>
        </FormField>
    );

    return (
        <FlexCol $gap={8}>
            <div />
            <Divider />
            {globalReset}
            <FlexCol $gap={8}>
                <div>
                    <TableTileColumnEdit
                        isGlobalStyle
                        columnVisualization={globalColumnVisualization}
                        updateColumnVisualization={updateGlobalColumnVisualization}
                    />
                </div>

                <div>
                    <TableTileRowEdit
                        rowKey={''}
                        isGlobalStyle
                        defaultStyle={DEFAULT_ROW_STYLE}
                        rowStyles={rowStyles}
                        updateRowStyles={updateRowStyles}
                    />
                </div>
            </FlexCol>

            {hasWaterfallColumns ? (
                <>
                    <Divider />

                    <FlexCol $gap={15}>
                        <Text>{LL.CHART_ROW_OPTIONS()}</Text>

                        <FlexCol $gap={4}>
                            {rowGroups.map((rows: SheetRowRepresentation[], rowIndex: number) => {
                                const [row] = rows;
                                const label = [...new Set(rows.map((row) => row.name))].join(', ');

                                const rowKey = rowPathToRowKey(row.path);
                                const currRowStyle = rowStyles[rowKey];

                                return (
                                    <ToggleField
                                        key={rowKey}
                                        size="small"
                                        title={LL.TOTAL()}
                                        label={label}
                                        disabled={rowIndex === 0}
                                        value={currRowStyle?.total || rowIndex === 0}
                                        onChange={() =>
                                            updateRowStyles({
                                                [rowKey]: { total: !currRowStyle?.total },
                                            })
                                        }
                                    />
                                );
                            })}
                        </FlexCol>
                    </FlexCol>
                </>
            ) : null}
        </FlexCol>
    );
}
